import { forwardRef, ImgHTMLAttributes } from 'react';
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';
import { cn } from '@/lib/utils';

interface StorageImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** Stored image URL (Supabase Storage, data:, or external). Null/undefined renders nothing. */
  src: string | null | undefined;
  /**
   * O que mostrar ENQUANTO a URL assinada é resolvida.
   *
   * O padrão é um esqueleto pulsante com as MESMAS classes da imagem, e não
   * `null`: os buckets são privados (#6), então toda foto passa por uma
   * assinatura antes de aparecer, e com `null` o espaço ficava simplesmente em
   * branco. Numa rede de celular isso são segundos em que a tela não diz nada e
   * o usuário conclui que a foto se perdeu — a mesma queixa que motivou o
   * indicador de upload no formulário de orçamento.
   *
   * Passe `fallback={null}` explicitamente quando o container já tiver o próprio
   * espaço reservado e um segundo retângulo pulsando fosse ruído.
   */
  fallback?: React.ReactNode;
}

/**
 * Renders a stored image after resolving it to a session-usable URL. Handles
 * data URLs (returned as-is), external hotlinks (returned as-is), and
 * Supabase Storage paths (signed on demand, cached briefly).
 *
 * Only mount when there is a URL to render — pair with `{src ? <StorageImage .../> : <fallback/>}`.
 */
export const StorageImage = forwardRef<HTMLImageElement, StorageImageProps>(
  ({ src, fallback, ...rest }, ref) => {
    const resolved = useSignedMediaUrl(src);
    if (!resolved) {
      if (fallback !== undefined) return <>{fallback}</>;
      // As classes da imagem vão para o esqueleto para ele ocupar exatamente a
      // mesma caixa — sem isso o layout salta quando a foto chega. `aria-hidden`
      // porque ele não é conteúdo: é a ausência dele.
      return (
        <div
          aria-hidden
          className={cn('animate-pulse bg-muted', rest.className)}
        />
      );
    }
    return <img ref={ref} src={resolved} {...rest} />;
  }
);

StorageImage.displayName = 'StorageImage';
